import { useCallback, useEffect, useRef, useState } from "react";
import { notificationsApi } from "../services/notifications.api";

interface Options {
  /** Polling interval in ms. Default 30s. Set to 0 to disable. */
  intervalMs?: number;
  /** Pause polling when tab is hidden. Default true. */
  pauseWhenHidden?: boolean;
}

/**
 * useUnreadCount — polls GET /users/me/notifications/unread-count.
 *
 * Keeps the header bell badge live without websockets. Polling is paused
 * when the tab is hidden (document.visibilityState !== "visible") so idle
 * tabs do not hammer the backend.
 */
export function useUnreadCount(options?: Options) {
  const intervalMs = options?.intervalMs ?? 30_000;
  const pauseWhenHidden = options?.pauseWhenHidden ?? true;

  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const fetchCount = useCallback(async () => {
    setIsLoading(true);
    try {
      const n = await notificationsApi.getUnreadCount();
      if (mountedRef.current) setCount(n);
      return n;
    } catch {
      return null;
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void fetchCount();

    if (!intervalMs) return () => {};

    const schedule = () => {
      timerRef.current = setTimeout(async () => {
        if (
          !pauseWhenHidden ||
          typeof document === "undefined" ||
          document.visibilityState === "visible"
        ) {
          await fetchCount();
        }
        if (mountedRef.current) schedule();
      }, intervalMs);
    };
    schedule();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void fetchCount();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchCount, intervalMs, pauseWhenHidden]);

  return {
    count,
    isLoading,
    refetch: fetchCount,
    /** Optimistic local decrement after marking one as read. */
    decrement: (by = 1) => setCount((c) => Math.max(0, c - by)),
    /** Optimistic local reset after mark-all-as-read. */
    reset: () => setCount(0),
  };
}
